import { nanoid } from "nanoid";
import { useContext } from "react";
import constants from "../../constants";
import { getDataFromStorage } from "../../util";
import useAxiosPrivate from "../useAxiosPrivate";
import CodingEditorHooks from "./CodingEditorHooks";
import AlertContext from "../../context/AlertContext";
import useCommonFunctionHooks from "../useCommonFunctionHooks";
import CreateSchemaTableHooks from "../CreateSchemaTableHooks";
import NotifyBody from "../../Components/Notify/NotifyBody";

export default function CodingTestHooks() {
  const Alert = useContext(AlertContext);

  const axios = useAxiosPrivate();
  const { parseCreateTable } = CreateSchemaTableHooks();
  const { handleBoilerPlateForNoteBook } = CodingEditorHooks();

  const {handleRegistration, getCatchBlockDetails, getHeaderDetailsForAxios } =
    useCommonFunctionHooks();

  const pendingRequests = new Map();

  const getRequestKey = (url, data) => {
    return `${url}-${JSON.stringify(data)}`;
  };

  const getTestApi = (data) => {
    if (Alert.testData.testtype === constants.libTypeIds.SQL) {
      if (Alert.testData.status === constants.QUESTION_STATUS.CONTINUE) {
        return "node/learner/course/test/report";
      } else {
        return "node/learner/course/test/get";
      }
    }

    if (data.single) {
      return "node/learner/cc/add";
    } else {
      return "node/learner/codingassignment/add";
    }
  };

  const getTestPayload = (data) => {
    let payload = {
      learnerid: getDataFromStorage("learnerid"),
    };

    if (Alert.testData.testtype === constants.libTypeIds.SQL) {
      Alert.setMcqAndDesTestAndLpId({
        lpid: Alert.lpId,
        testid: Alert.testData.testid,
        cellobj: Alert.testData.libtype,
        autoEval: Alert.testData.autoeval,
        testmode: Alert.testData.testmode,
        viewTest: Alert.testData.viewTest,
        libtypeid: Alert.testData.testtype,
        proctored: Alert.testData.proctored,
        configdata: Alert.testData.configdata,
        lpconfigid: Alert.testData.lpconfigid,
        learnerid: getDataFromStorage("learnerid"),
        status: Alert.status,
      });
      payload.isTakingTest = true;
      payload.lmpid = Alert.testData.lmpid;
      payload.testid = Alert.testData.testid;
      payload.mockuse = Alert.testData.mockuse;
      payload.withAI = Alert.testData.ainterview;
      payload.libtypeid = Alert.testData.testtype;
      payload.lpid = sessionStorage.getItem("lpId");
      payload.lpconfigid = Alert.testData.lpconfigid;
      payload.mocklp = localStorage.getItem("mockLp");
    } else if (data.single) {
      payload.secid = data.secid;
      payload.questionid = data.questionid;
    } else {
      payload.topicid = data.topicid;
      payload.lmtid = Alert.testData.data.lmpid;
      payload.lmpid = Alert.testData.data.lmtid;
      payload.testconfigid = data.testconfigid;
      payload.mockuse = Alert.testData.mockuse;
      payload.lpid = sessionStorage.getItem("lpId");
      payload.lpconfigid = Alert.testData.lpconfigid;
      payload.completedqncount = data.completedcount;
      payload.mocklp = localStorage.getItem("mockLp");
    }

    return JSON.stringify(payload);
  };

  const resetCCTest = (data) => {
    // handling practice retake

    axios
      .post(
        "/node/learner/course/test/reset",
        JSON.stringify({
          testid: Alert.testData.testid,
          lpconfigid: Alert.testData.lpconfigid,
          learnerid: getDataFromStorage("learnerid"),
          testconfigid: data.testconfigid,
        }),
        getHeaderDetailsForAxios()
      )
      .then((res) => {
        if (res.data.resultCode === constants.RESULT_STATUS.SUCCESS) {
          data.status = constants.QUESTION_STATUS.CONTINUE;
          Alert.setStatus(constants.QUESTION_STATUS.CONTINUE);

          Alert.testData.data.topics.forEach((topic) => {
            topic.status = constants.QUESTION_STATUS.CONTINUE;
          });

          takeTest(data);
        }
      })
      .catch((error) => {
        getCatchBlockDetails(error);
      })
      .finally(() => {
        Alert.setIsLoaded(false);
        Alert.setButtonDisable(false);
      });
  };

  const updateStatus = (data) => {
    const duration = `${Math.floor(
      (parseInt(data.duration, 10) + 5 * 60) / 60
    )}m`;

    axios
      .post(
        "/node/learner/dp/status/update",
        JSON.stringify({
          qnId: data.qnId,
          status: data.status,
          libType: data.libType,
          testId: data?.testId || 0,
          duration: duration || "20m",
          userId: getDataFromStorage("learnerid"),
        }),
        getHeaderDetailsForAxios()
      )
      .catch((error) => {
        getCatchBlockDetails(error);
      })
      .finally(() => {
        Alert.setIsLoaded(false);
        Alert.setButtonDisable(false);
      });
  };

  const takeTest = (data) => {
    const url = getTestApi(data);
    let payload = getTestPayload(data);
    const requestKey = getRequestKey(url, payload);
    console.log(
      "data........",
      data,
      "payload.......",
      payload,
      "requestKey........",
      requestKey
    );

    if (pendingRequests.has(requestKey)) {
      console.log("check", pendingRequests);

      const controller = pendingRequests.get(requestKey);

      controller.abort();
      pendingRequests.delete(requestKey);
    }

    const controller = new AbortController();

    pendingRequests.set(requestKey, controller);

    Alert.setIsLoaded(true);
    Alert.setButtonDisable(true);
    payload = JSON.parse(payload ?? "{}");

    axios
      .post(url, payload, {
        signal: controller.signal,
        ...getHeaderDetailsForAxios(),
      })
      .then((res) => {
        if(res.data.resultCode  === constants.RESULT_STATUS.FACE_NOT_REGISTERED){
          Alert.setIsLoaded(false);

          Alert.setShowNotify({
            show: true,
            size: "lg",
            primaryButton: "Continue",
            secondaryButton: "Cancel",
            primaryButtonFunction: handleRegistration,
            title: "First time taking a proctored test.",
            msg: <NotifyBody component="instructionPageNotifyMsgForRegistration" />,
          });
              
        } else if (res.data.resultCode !== constants.RESULT_STATUS.SUCCESS) {
          Alert.setIsLoaded(false);

          Alert.setShowNotify({
            show: true,
            title: "Info",
            msg: res.data.msg,
          });
          Alert.setResultCode(res.data.resultCode);
        } else {
          if (
            Alert.testData.testmode === constants.MODE.TEST_MODE &&
            Alert.testData.proctored !== constants.PROCTORING_MODE.TAB
          ) {
            if (Alert.testData.proctored === constants.PROCTORING_MODE.DUAL) {
              updateStatus({
                status: 2,
                qnId: res.data.data.qnid,
                testId: Alert.testData.testid,
                duration: res.data.data.duration,
                libType: Alert.testData.testtype,
              });
            }

            Alert.setIsTakeTest(true);
          }

          if (Alert.testData.testtype === constants.libTypeIds.SQL) {
            res.data.data.sections[0].questions =
              res.data.data.sections[0]?.questions.map(
                ({ answer, ...rest }, _, arr) => ({
                  ...rest,
                  solution: answer,
                })
              );

            Alert.setTimeLimit(Alert.testData.configdata.duration);
            Alert.duration.current = Alert.testData.configdata.duration;
            Alert.updateComingObj(res.data.data);
            Alert.setTestDetail(() => ({
              currentIndex: 0,
              data: res.data.data,
              totalQn: res.data.data.sections[0]?.questions.length,
              schema: parseCreateTable(
                res.data.data.sections[0]?.questions[0].dbSchema
              ),
              ...(res.data.data.sections[0]?.questions[0] || {}),
              language: [
                {
                  value: 1,
                  label: "SQL",
                },
              ],
              selectedLanguage: {
                value: 1,
                label: "SQL",
              },
            }));

            Alert.setIsLoaded(false);
            Alert.navigate("/sql");
          } else {
            let totalCount = 0;

            let response = res.data.data,
              testCases =
                response.testcases !== null
                  ? typeof response.testcases === "string"
                    ? JSON.parse(response.testcases)
                    : response.testcases
                  : [];

            testCases = testCases.filter((example) => example.isexample === 1);

            for (let j = 0; j < testCases.length; j++) {
              if (j === 0) testCases[j].isActive = true;
              testCases[j].id = nanoid();
            }

            response.testcases = JSON.stringify(testCases);
            console.log("response.templatecode", response.templatecode, typeof response.templatecode);
            
            if (
              data.status === constants.QUESTION_STATUS.ASSIGNED ||
              data.status === constants.QUESTION_STATUS.RESUBMIT || 
              data.status === constants.QUESTION_STATUS.REATTEMPT_NEW_VERSION
            ) {
              for (let k = 0; k < response.templatecode.length; k++) {
                /*
                usercode => student written code i.e(saved one)
                code=>boilerplate code
                userCode => current student code before submiting
                */
                response.templatecode[k].userCode = Alert.testData?.notebook
                  ? handleBoilerPlateForNoteBook(
                      response.templatecode[k].langid
                    )
                  :
                  response.templatecode[k].templateversion === 2
                    ? response.templatecode[k].executioncode
                    : response.templatecode[k].code;
              }
            }

            response.isChangeCode = false;

            if (!data.hasOwnProperty("lccid")) {
              for (let i = 0; i < Alert.testData.data.topics.length; i++) {
                totalCount += Alert.testData.data.topics[i].totalcount;
              }

              response.topics = data;
              response.secData = data;
              response.lpId = Alert.lpId;
              response.status = data.status;
              response.secData.totalCount = totalCount;
              response.testname = Alert.testData.testname;
              response.lpConfigId = Alert.testData.lpconfigid;
              response.secData.testId = Alert.testData.testid;
              response.secData.testMode = Alert.testData.testmode;
              response.secData.proctored = Alert.testData.proctored;
              response.secData.completedcount = data.completedcount;
            }

            
            if(data.hasOwnProperty("lccid")){
              //updating the latest lccid for saveans api
              Alert.setTestData((prev)=>({
                ...prev,
                lccid:response.lccid
              }))
            }

            Alert.setIsLoaded(false);
            Alert.assignQuestionToLearner(response);
          }
        }
      })
      .catch((error) => {
        if (document.fullscreenElement) {
          document.exitFullscreen();
        }

        if (error.name === "AbortError") {
          console.warn("Duplicate request aborted:", requestKey);
        } else {
          getCatchBlockDetails(error);
        }
      })
      .finally(() => {
        pendingRequests.delete(requestKey);
        Alert.setButtonDisable(false);
      });
  };

  return { takeTest, resetCCTest, updateStatus };
}
